import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getDuplicateCleanupReport, markDuplicate, unflagDuplicate, getDuplicateCleanupLogs, autoFlagDuplicates } from '../api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../components/ui/tabs';
import {
  AlertTriangle, RefreshCw, ShieldCheck, ShieldAlert, CheckCircle2, Search, History as HistoryIcon, Zap, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const FIELD_LABELS = {
  employee_id: 'Employee ID',
  aadhaar_number: 'Aadhaar',
  pan_number: 'PAN',
  email: 'Email',
  phone: 'Phone',
  name: 'Name',
};

const FIELD_COLORS = {
  employee_id: 'bg-red-100 text-red-700 border-red-300',
  pan_number: 'bg-orange-100 text-orange-700 border-orange-300',
  aadhaar_number: 'bg-amber-100 text-amber-700 border-amber-300',
  email: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  name: 'bg-slate-100 text-slate-700 border-slate-300',
  phone: 'bg-blue-100 text-blue-700 border-blue-300',
};

const DuplicateCleanupReport = ({ user, setUser }) => {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [logs, setLogs] = useState([]);
  const [fieldFilter, setFieldFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [markDialog, setMarkDialog] = useState(null); // {keep, discard}
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Auto-flag state (Round 82)
  const [autoFlagOpen, setAutoFlagOpen] = useState(false);
  const [autoFlagPreview, setAutoFlagPreview] = useState(null);
  const [autoFlagReason, setAutoFlagReason] = useState('');
  const [autoFlagLoading, setAutoFlagLoading] = useState(false);

  const canAct = user?.role === 'admin' || user?.role === 'hr_manager';

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [fieldFilter, debouncedSearch]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (fieldFilter !== 'all') params.field = fieldFilter;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      const [r, l] = await Promise.all([
        getDuplicateCleanupReport(params),
        getDuplicateCleanupLogs(200),
      ]);
      setReport(r.data);
      setLogs(l.data.logs || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, fieldFilter, debouncedSearch]);

  const summary = report?.summary || {};
  const pagination = report?.pagination || { filtered_total: 0, total_pages: 1 };
  const filteredGroups = report?.groups || [];

  const openMarkDialog = (keep, discard) => {
    setMarkDialog({ keep, discard });
    setReason('');
  };

  const submitMark = async () => {
    if (!markDialog || !reason.trim()) { toast.error('Reason is required'); return; }
    setSubmitting(true);
    try {
      await markDuplicate({
        keep_employee_id: markDialog.keep.id,
        discard_employee_id: markDialog.discard.id,
        reason: reason.trim(),
      });
      toast.success(`${markDialog.discard.employee_id} flagged as duplicate of ${markDialog.keep.employee_id}`);
      setMarkDialog(null);
      setReason('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnflag = async (empId) => {
    if (!window.confirm('Unflag this record as a duplicate?')) return;
    try {
      await unflagDuplicate(empId);
      toast.success('Unflagged');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const openAutoFlag = async () => {
    setAutoFlagOpen(true);
    setAutoFlagReason('');
    setAutoFlagPreview(null);
    setAutoFlagLoading(true);
    try {
      const r = await autoFlagDuplicates({ reason: 'preview', dry_run: true });
      setAutoFlagPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
      setAutoFlagOpen(false);
    } finally {
      setAutoFlagLoading(false);
    }
  };

  const executeAutoFlag = async () => {
    if (!autoFlagReason.trim()) { toast.error('Reason is required'); return; }
    if (!autoFlagPreview || !autoFlagPreview.total_to_flag) return;
    if (!window.confirm(`Flag ${autoFlagPreview.total_to_flag} records as duplicates? This is safe (soft-flag) and can be undone from the Audit tab.`)) return;
    setAutoFlagLoading(true);
    try {
      const r = await autoFlagDuplicates({ reason: autoFlagReason.trim(), dry_run: false });
      toast.success(`Flagged ${r.data.total_flagged} records`);
      setAutoFlagOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auto-flag failed');
    } finally {
      setAutoFlagLoading(false);
    }
  };

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="duplicate-cleanup-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
              Duplicate Cleanup Report
            </h1>
            <p className="text-sm text-slate-500">
              Identifies existing employee records sharing the same Employee ID, Aadhaar, PAN, Email, Phone, or Name.
              Safe soft-flag action leaves historical data intact.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canAct && (summary.total_groups || 0) > 0 && (
              <Button
                onClick={openAutoFlag}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="bulk-auto-flag-btn"
              >
                <Zap className="h-4 w-4 mr-1" />
                Bulk Auto-Flag
              </Button>
            )}
            <Button onClick={load} variant="outline" data-testid="refresh-btn" disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Rescan
            </Button>
          </div>
        </div>

        {/* KPI CARDS */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Card data-testid="kpi-scanned">
            <CardContent className="pt-5">
              <p className="text-xs text-slate-500">Employees Scanned</p>
              <p className="text-2xl font-bold text-slate-900">{report?.total_employees_scanned || 0}</p>
            </CardContent>
          </Card>
          <Card data-testid="kpi-groups">
            <CardContent className="pt-5">
              <p className="text-xs text-slate-500">Duplicate Groups</p>
              <p className="text-2xl font-bold text-red-700">{summary.total_groups || 0}</p>
            </CardContent>
          </Card>
          {Object.entries(summary.by_field || {}).map(([field, count]) => (
            <Card key={field} data-testid={`kpi-by-${field}`}>
              <CardContent className="pt-5">
                <p className="text-xs text-slate-500">By {FIELD_LABELS[field] || field}</p>
                <p className={`text-2xl font-bold ${count > 0 ? 'text-orange-700' : 'text-slate-400'}`}>{count}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs defaultValue="groups">
          <TabsList>
            <TabsTrigger value="groups" data-testid="tab-groups">
              Duplicate Groups ({filteredGroups.length})
            </TabsTrigger>
            <TabsTrigger value="logs" data-testid="tab-logs">
              <HistoryIcon className="h-4 w-4 mr-1" />Audit Log ({logs.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="groups" className="space-y-4">
            {/* FILTERS */}
            <Card>
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Search</Label>
                    <div className="relative">
                      <Search className="h-3 w-3 absolute left-2 top-2.5 text-slate-400" />
                      <Input
                        className="pl-7"
                        placeholder="Employee ID, name, email, phone..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        data-testid="search-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Field</Label>
                    <Select value={fieldFilter} onValueChange={setFieldFilter}>
                      <SelectTrigger data-testid="filter-field"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All fields</SelectItem>
                        {Object.entries(FIELD_LABELS).map(([v, l]) => (
                          <SelectItem key={v} value={v}>{l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end text-xs text-slate-500">
                    Showing <b className="mx-1">{filteredGroups.length}</b> of <b className="mx-1">{pagination.filtered_total || 0}</b> matching · page {pagination.page || 1}/{pagination.total_pages || 1}
                  </div>
                </div>
                {/* Pagination controls */}
                {(pagination.total_pages || 1) > 1 && (
                  <div className="flex items-center justify-end gap-2 mt-3">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={page <= 1 || loading}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      data-testid="pg-prev"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-xs text-slate-600">Page {pagination.page || 1} of {pagination.total_pages || 1}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={page >= (pagination.total_pages || 1) || loading}
                      onClick={() => setPage((p) => p + 1)}
                      data-testid="pg-next"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* GROUPS */}
            {filteredGroups.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  {loading ? (
                    <p className="text-slate-500">Scanning...</p>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <CheckCircle2 className="h-12 w-12 text-green-500" />
                      <p className="text-green-700 font-medium">No duplicate groups found</p>
                      <p className="text-xs text-slate-500">Your employee master is clean.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              filteredGroups.map((g, i) => (
                <Card key={`${g.field}-${g.value}-${i}`} data-testid={`group-${i}`}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-0.5 text-xs rounded border ${FIELD_COLORS[g.field] || FIELD_COLORS.name}`}>
                        {FIELD_LABELS[g.field] || g.field}
                      </span>
                      <span className="font-mono text-sm">{g.value}</span>
                      <span className="ml-auto text-xs font-normal text-slate-500">{g.count} records</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {g.records.map((r) => (
                        <div key={r.id} className="border rounded-lg p-3 bg-slate-50 space-y-1" data-testid={`record-${r.id}`}>
                          <div className="flex items-start justify-between">
                            <div className="text-sm">
                              <p className="font-semibold text-indigo-800">{r.employee_id}</p>
                              <p className="text-slate-800">{r.name}</p>
                            </div>
                            <span className={`px-2 py-0.5 text-[10px] rounded uppercase ${
                              r.employment_status === 'working' ? 'bg-green-100 text-green-700'
                              : r.employment_status === 'exited' ? 'bg-red-100 text-red-700'
                              : 'bg-slate-200 text-slate-700'
                            }`}>{r.employment_status}</span>
                          </div>
                          <div className="text-xs text-slate-600 space-y-0.5">
                            <p>📧 {r.email || '—'}</p>
                            <p>📞 {r.phone || '—'}</p>
                            <p>🪪 PAN: {r.pan_number || '—'}  ·  Aadhaar: {r.aadhaar_number || '—'}</p>
                            <p>🏢 {r.designation || '—'}, {r.department || '—'}</p>
                            <p>📅 DOJ: {r.date_of_joining || '—'}  ·  Created: {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</p>
                          </div>
                          {canAct && (
                            <div className="pt-2 flex gap-2">
                              {g.records.filter((other) => other.id !== r.id).map((other) => (
                                <Button
                                  key={other.id}
                                  size="sm"
                                  variant="outline"
                                  className="text-xs h-7 border-red-300 text-red-700 hover:bg-red-50"
                                  onClick={() => openMarkDialog(other, r)}
                                  data-testid={`mark-dup-${r.id}-keep-${other.id}`}
                                >
                                  <ShieldAlert className="h-3 w-3 mr-1" />
                                  Mark as dup of {other.employee_id}
                                </Button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="logs">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Audit Log</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2 text-xs font-medium">When</th>
                        <th className="text-left p-2 text-xs font-medium">Action</th>
                        <th className="text-left p-2 text-xs font-medium">Kept</th>
                        <th className="text-left p-2 text-xs font-medium">Flagged as Dup</th>
                        <th className="text-left p-2 text-xs font-medium">By</th>
                        <th className="text-left p-2 text-xs font-medium">Reason</th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <tr key={log.id} className="border-b hover:bg-slate-50">
                          <td className="p-2 text-xs">{log.created_at ? new Date(log.created_at).toLocaleString() : '—'}</td>
                          <td className="p-2 text-xs uppercase">{log.action}</td>
                          <td className="p-2 text-xs">
                            <p className="font-medium">{log.keep_employee_code}</p>
                            <p className="text-slate-500">{log.keep_employee_name}</p>
                          </td>
                          <td className="p-2 text-xs">
                            <p className="font-medium">{log.discard_employee_code}</p>
                            <p className="text-slate-500">{log.discard_employee_name}</p>
                          </td>
                          <td className="p-2 text-xs">{log.performed_by_user_name}</td>
                          <td className="p-2 text-xs italic text-slate-600">{log.reason}</td>
                          <td className="p-2">
                            {canAct && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleUnflag(log.discard_employee_id)}
                                className="text-xs h-7"
                                data-testid={`unflag-${log.discard_employee_id}`}
                              >
                                <ShieldCheck className="h-3 w-3 mr-1" />Unflag
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {logs.length === 0 && (
                        <tr><td colSpan="7" className="p-8 text-center text-slate-500">No cleanup actions logged yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Bulk Auto-Flag preview dialog (Round 82) */}
        <Dialog open={autoFlagOpen} onOpenChange={(v) => !v && setAutoFlagOpen(false)}>
          <DialogContent className="max-w-2xl" data-testid="auto-flag-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-red-600" />
                Bulk Auto-Flag Duplicates
              </DialogTitle>
              <DialogDescription>
                Automatically flag exact-match duplicates (Employee ID, PAN, Aadhaar, Email). Keeper = most-complete record.
                Soft-flag only — nothing is deleted; reversible from the Audit tab.
              </DialogDescription>
            </DialogHeader>
            {autoFlagLoading && !autoFlagPreview && (
              <p className="text-sm text-slate-500 py-6 text-center">Computing preview...</p>
            )}
            {autoFlagPreview && (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-red-50 border border-red-200 rounded">
                    <p className="text-xs text-red-800">Records to flag</p>
                    <p className="text-2xl font-bold text-red-700" data-testid="auto-flag-count">{autoFlagPreview.total_to_flag}</p>
                  </div>
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                    <p className="text-xs text-amber-800">Groups affected</p>
                    <p className="text-2xl font-bold text-amber-700">{autoFlagPreview.groups_affected}</p>
                  </div>
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded">
                    <p className="text-xs text-slate-500">Fields</p>
                    <p className="text-xs font-mono">{(autoFlagPreview.fields_used || []).join(', ')}</p>
                  </div>
                </div>
                {autoFlagPreview.total_to_flag > 0 && (
                  <>
                    <div className="max-h-56 overflow-y-auto border rounded" data-testid="auto-flag-sample">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-100 sticky top-0">
                          <tr>
                            <th className="p-2 text-left">Keep</th>
                            <th className="p-2 text-left">Flag as dup</th>
                            <th className="p-2 text-left">Matched on</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(autoFlagPreview.preview_sample || []).map((d, i) => (
                            <tr key={i} className="border-b">
                              <td className="p-2"><b>{d.keeper_code}</b><br /><span className="text-slate-500">{d.keeper_name}</span></td>
                              <td className="p-2 text-red-700"><b>{d.discard_code}</b><br /><span className="text-slate-500">{d.discard_name}</span></td>
                              <td className="p-2 text-slate-600">{d.field}: <span className="font-mono text-[10px]">{d.value}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {autoFlagPreview.preview_truncated && (
                      <p className="text-[11px] text-slate-500 text-center">Showing first 50 — {autoFlagPreview.total_to_flag - 50} more will also be flagged.</p>
                    )}
                    <div>
                      <Label>Audit Reason (required)</Label>
                      <Input
                        value={autoFlagReason}
                        onChange={(e) => setAutoFlagReason(e.target.value)}
                        placeholder="e.g. Bulk import Excel float artifact (255967269927.0 vs 255967269927)"
                        data-testid="auto-flag-reason"
                      />
                    </div>
                  </>
                )}
                {autoFlagPreview.total_to_flag === 0 && (
                  <p className="p-4 text-center text-slate-500">
                    <CheckCircle2 className="h-8 w-8 text-green-500 inline" />
                    <br />No exact duplicates found on Employee ID / PAN / Aadhaar / Email.
                  </p>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setAutoFlagOpen(false)}>Cancel</Button>
              {autoFlagPreview && autoFlagPreview.total_to_flag > 0 && (
                <Button
                  onClick={executeAutoFlag}
                  disabled={autoFlagLoading || !autoFlagReason.trim()}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="confirm-auto-flag"
                >
                  {autoFlagLoading ? 'Flagging...' : `Flag ${autoFlagPreview.total_to_flag} records`}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Mark-as-duplicate confirmation dialog */}
        <Dialog open={!!markDialog} onOpenChange={(v) => !v && setMarkDialog(null)}>
          <DialogContent className="max-w-lg" data-testid="mark-dup-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldAlert className="h-5 w-5 text-red-600" />
                Mark as Duplicate
              </DialogTitle>
              <DialogDescription>
                This is a soft-flag: the discarded record stays in the DB (attendance, leaves, payroll history remain).
                It will be excluded from future duplicate scans and can be unflagged from the Audit tab.
              </DialogDescription>
            </DialogHeader>
            {markDialog && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 border-2 border-green-400 bg-green-50 rounded" data-testid="keep-card">
                    <p className="text-[10px] uppercase text-green-700 font-medium mb-1">Keep ✓</p>
                    <p className="font-semibold">{markDialog.keep.employee_id}</p>
                    <p className="text-sm">{markDialog.keep.name}</p>
                    <p className="text-[10px] text-slate-500">{markDialog.keep.email}</p>
                  </div>
                  <div className="p-3 border-2 border-red-400 bg-red-50 rounded" data-testid="discard-card">
                    <p className="text-[10px] uppercase text-red-700 font-medium mb-1">Flag as Duplicate ✗</p>
                    <p className="font-semibold">{markDialog.discard.employee_id}</p>
                    <p className="text-sm">{markDialog.discard.name}</p>
                    <p className="text-[10px] text-slate-500">{markDialog.discard.email}</p>
                  </div>
                </div>
                <div>
                  <Label>Reason (required)</Label>
                  <Input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Same person re-added by mistake / typo in original entry"
                    data-testid="mark-dup-reason"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setMarkDialog(null)}>Cancel</Button>
              <Button onClick={submitMark} disabled={submitting || !reason.trim()} className="bg-red-600 hover:bg-red-700" data-testid="confirm-mark-dup">
                {submitting ? 'Flagging...' : 'Confirm & Flag'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DuplicateCleanupReport;
